const FILMSTRIP_WORKER_PATH = "./build/workers/generateFilmstripFrame.js";
const FILMSTRIP_CACHE_DIR = "cc-creator-filmstrip";

/** Default generated thumbnail width in px (must match TimelineFilmstrip). */
export const FILMSTRIP_FRAME_WIDTH = 160;

function sanitizeSegment(value) {
    return String(value || "unknown").replace(/[^a-z0-9._-]+/gi, "-").replace(/^-+|-+$/g, "") || "unknown";
}

function isVideoMedia(media) {
    return typeof media?.type === "string" && media.type.startsWith("video");
}

function isLocalFilesystemPath(localPath) {
    if (!localPath || typeof localPath !== "string") return false;
    // ffmpeg input for the filmstrip worker must be a local file. Reject
    // http(s)/blob/data URLs and already-normalized file:// URLs; the Cloud
    // Storage import stores the remote HTTPS URL in `localPath`.
    if (/^(https?|blob|data|file):/i.test(localPath)) return false;
    return true;
}

/**
 * Desktop-only prerequisites for generating film strip frames: a Worker,
 * ffmpeg, the Node fs/path/os bridges from preload, and local video media.
 */
export function canGenerateFilmstrip(projectState) {
    return Boolean(
        typeof window !== "undefined" &&
        window.Worker &&
        window.ffmpegPath &&
        window.fs &&
        window.path &&
        window.os &&
        isLocalFilesystemPath(projectState?.media?.localPath) &&
        isVideoMedia(projectState.media)
    );
}

/**
 * Normalize a local file path into a `file://` URL that an HTMLImageElement /
 * Konva Image can load. Passes through data/file/http(s) URLs unchanged.
 */
export function getFilmstripFrameSrc(thumbnailPath) {
    if (!thumbnailPath) {
        return "";
    }

    if (/^(data|file|https?):/i.test(thumbnailPath)) {
        return thumbnailPath;
    }

    // Match peaks.js / Electron conventions: `file:///C:/...` on Windows.
    const normalized = thumbnailPath.replace(/\\/g, "/");
    return /^[a-z]:\//i.test(normalized) ? `file:///${normalized}` : `file://${normalized}`;
}

/**
 * Build the ordered list of frame descriptors centered on `startTime`:
 * `frameCount` frames before, the start frame, and `frameCount` frames after,
 * for a total of `2 * frameCount + 1` frames. Times are spaced by one frame
 * (1 / frameRate) and clamped into the legal media range.
 *
 * @returns {{ index: number, offset: number, time: number, isStart: boolean }[]}
 */
export function computeFilmstripFrameTimes(startTime, frameCount, frameRate, duration) {
    const fps = Number(frameRate) > 0 ? Number(frameRate) : 25;
    const count = Math.min(30, Math.max(1, Math.round(Number(frameCount) || 1)));
    const maxTime = Number.isFinite(duration) && duration > 0 ? duration : Infinity;
    const frames = [];
    for (let offset = -count; offset <= count; offset += 1) {
        const rawTime = startTime + offset / fps;
        const time = Math.max(0, Math.min(rawTime, maxTime));
        frames.push({
            index: offset + count,
            offset,
            time,
            isStart: offset === 0
        });
    }
    return frames;
}

function getCacheDir(projectState) {
    const projectKey = sanitizeSegment(projectState.id || projectState.name || "project");
    return window.path.join(window.os.tmpdir(), FILMSTRIP_CACHE_DIR, projectKey);
}

function getFrameFileName(projectState, timeMs, width) {
    const media = projectState.media || {};
    const mediaName = media.name || window.path.basename(media.localPath || "media");
    return [
        sanitizeSegment(mediaName),
        Math.round(timeMs),
        `w${Math.round(width)}`
    ].join("-") + ".jpg";
}

function normalizePath(pathValue) {
    return String(pathValue || "").replace(/\\/g, "/");
}

async function frameExists(outputPath) {
    try {
        await window.fs.access(outputPath);
        return true;
    } catch {
        return false;
    }
}

/**
 * Generate (or load from disk cache) the film strip frames around an event's
 * start time. Returns a cancelable job whose `onFrame` callback fires once per
 * frame as it becomes available (cached frames resolve immediately, missing
 * frames after the ffmpeg worker extracts them).
 *
 * @param {Object} params
 * @param {Object} params.projectState
 * @param {number} params.startTime - event start time in seconds
 * @param {number} params.frameCount - frames before/after the start frame
 * @param {number} [params.width] - generated frame width in px
 * @param {number} [params.duration] - media duration in seconds (for clamping)
 * @param {(frame: { index: number, offset: number, time: number, isStart: boolean, src: string }) => void} [params.onFrame]
 * @returns {{ promise: Promise<Array>, cancel: () => void }}
 */
export function generateFilmstrip({ projectState, startTime, frameCount, width = FILMSTRIP_FRAME_WIDTH, duration, onFrame } = {}) {
    let cancelled = false;
    let worker = null;

    const run = async () => {
        if (!canGenerateFilmstrip(projectState) || !Number.isFinite(Number(startTime))) {
            return [];
        }

        const frameRate = projectState.frameRate;
        const mediaDuration = Number.isFinite(duration)
            ? duration
            : (projectState?.media?.duration ?? projectState?.duration);
        const frames = computeFilmstripFrameTimes(startTime, frameCount, frameRate, mediaDuration);

        const cacheDir = getCacheDir(projectState);
        await window.fs.mkdir(cacheDir, { recursive: true });
        if (cancelled) return frames;

        for (const frame of frames) {
            frame.timeMs = Math.round(frame.time * 1000);
            frame.outputPath = window.path.join(cacheDir, getFrameFileName(projectState, frame.timeMs, width));
            frame.src = "";
        }

        // Resolve cached frames immediately; collect missing ones (deduped by
        // output path, since boundary clamping can map several offsets to the
        // same time) for the worker to extract.
        const needByPath = new Map();
        for (const frame of frames) {
            if (cancelled) return frames;
            if (await frameExists(frame.outputPath)) {
                frame.src = getFilmstripFrameSrc(frame.outputPath);
                onFrame?.({ ...frame });
            } else if (!needByPath.has(frame.outputPath)) {
                needByPath.set(frame.outputPath, {
                    index: frame.index,
                    time: frame.time,
                    outputPath: frame.outputPath
                });
            }
        }

        const toGenerate = [...needByPath.values()];
        if (cancelled || toGenerate.length === 0) {
            return frames;
        }

        await new Promise((resolve) => {
            worker = new Worker(FILMSTRIP_WORKER_PATH);

            worker.onmessage = (msg) => {
                const data = msg.data || {};
                if (data.result === "frame") {
                    const thumbPath = normalizePath(data.thumbnailPath);
                    const src = getFilmstripFrameSrc(data.thumbnailPath);
                    for (const frame of frames) {
                        if (normalizePath(frame.outputPath) === thumbPath) {
                            frame.src = src;
                            onFrame?.({ ...frame });
                        }
                    }
                } else if (data.result === "done" || data.result === "error") {
                    if (worker) {
                        worker.terminate();
                        worker = null;
                    }
                    resolve();
                }
            };

            worker.onerror = () => {
                if (worker) {
                    worker.terminate();
                    worker = null;
                }
                resolve();
            };

            worker.postMessage({
                inputPath: projectState.media.localPath,
                ffmpegPath: window.ffmpegPath,
                width,
                frames: toGenerate
            });
        });

        return frames;
    };

    const promise = run();

    return {
        promise,
        cancel() {
            cancelled = true;
            if (worker) {
                worker.terminate();
                worker = null;
            }
        }
    };
}

/**
 * Batch-warm the filmstrip disk cache for many event start times in one
 * cancelable worker job. Collects unique frame output paths across all starts
 * (using the same cache keys as `generateFilmstrip`), resolves hits
 * immediately, and extracts only misses.
 *
 * @param {Object} params
 * @param {Object} params.projectState
 * @param {number[]} params.startTimes - event start times in seconds
 * @param {number} params.frameCount - frames before/after each start
 * @param {number} [params.width]
 * @param {number} [params.duration]
 * @param {(info: { completed: number, total: number, cached: number, generated: number }) => void} [params.onProgress]
 * @returns {{ promise: Promise<{ total: number, cached: number, generated: number, cancelled: boolean }>, cancel: () => void }}
 */
export function preRenderFilmstripCache({
    projectState,
    startTimes = [],
    frameCount,
    width = FILMSTRIP_FRAME_WIDTH,
    duration,
    onProgress
} = {}) {
    let cancelled = false;
    let worker = null;
    let resolveWorker = null;

    const run = async () => {
        const result = { total: 0, cached: 0, generated: 0, cancelled: false };

        if (!canGenerateFilmstrip(projectState)) {
            return result;
        }

        const frameRate = projectState.frameRate;
        const mediaDuration = Number.isFinite(duration)
            ? duration
            : (projectState?.media?.duration ?? projectState?.duration);

        const cacheDir = getCacheDir(projectState);
        await window.fs.mkdir(cacheDir, { recursive: true });
        if (cancelled) {
            result.cancelled = true;
            return result;
        }

        // Flatten all start times into unique frames keyed by output path.
        const framesByPath = new Map();
        let nextIndex = 0;
        for (const startTime of startTimes) {
            if (!Number.isFinite(Number(startTime))) continue;
            const descriptors = computeFilmstripFrameTimes(
                Number(startTime),
                frameCount,
                frameRate,
                mediaDuration
            );
            for (const descriptor of descriptors) {
                const timeMs = Math.round(descriptor.time * 1000);
                const outputPath = window.path.join(
                    cacheDir,
                    getFrameFileName(projectState, timeMs, width)
                );
                const key = normalizePath(outputPath);
                if (framesByPath.has(key)) continue;
                framesByPath.set(key, {
                    index: nextIndex++,
                    time: descriptor.time,
                    timeMs,
                    outputPath
                });
            }
        }

        const frames = [...framesByPath.values()];
        result.total = frames.length;
        if (result.total === 0 || cancelled) {
            result.cancelled = cancelled;
            onProgress?.({ completed: 0, total: 0, cached: 0, generated: 0 });
            return result;
        }

        const needByPath = new Map();
        let completed = 0;

        for (const frame of frames) {
            if (cancelled) {
                result.cancelled = true;
                return result;
            }
            if (await frameExists(frame.outputPath)) {
                result.cached += 1;
                completed += 1;
                onProgress?.({
                    completed,
                    total: result.total,
                    cached: result.cached,
                    generated: result.generated
                });
            } else {
                needByPath.set(normalizePath(frame.outputPath), {
                    index: frame.index,
                    time: frame.time,
                    outputPath: frame.outputPath
                });
            }
        }

        const toGenerate = [...needByPath.values()];
        if (cancelled) {
            result.cancelled = true;
            return result;
        }
        if (toGenerate.length === 0) {
            return result;
        }

        await new Promise((resolve) => {
            worker = new Worker(FILMSTRIP_WORKER_PATH);
            const finish = () => {
                if (worker) {
                    worker.terminate();
                    worker = null;
                }
                resolveWorker = null;
                resolve();
            };
            resolveWorker = finish;

            worker.onmessage = (msg) => {
                const data = msg.data || {};
                if (data.result === "frame" || data.result === "frameError") {
                    if (data.result === "frame") {
                        result.generated += 1;
                    }
                    completed += 1;
                    onProgress?.({
                        completed,
                        total: result.total,
                        cached: result.cached,
                        generated: result.generated
                    });
                } else if (data.result === "done" || data.result === "error") {
                    finish();
                }
            };

            worker.onerror = () => {
                finish();
            };

            worker.postMessage({
                inputPath: projectState.media.localPath,
                ffmpegPath: window.ffmpegPath,
                width,
                frames: toGenerate
            });
        });

        if (cancelled) {
            result.cancelled = true;
        }
        return result;
    };

    const promise = run();

    return {
        promise,
        cancel() {
            cancelled = true;
            if (resolveWorker) {
                resolveWorker();
            } else if (worker) {
                worker.terminate();
                worker = null;
            }
        }
    };
}
